import React from 'react';
import { SearchX, AlertTriangle, Loader2, X } from 'lucide-react';
import { TYPE, FOCUS, SURFACE } from './tokens';

/**
 * Nothing found, something broke, and something is happening.
 *
 * All three used to be the same thing: a line of grey prose. A dead end with no way forward is
 * where people give up on an assistant, so each of these states names what happened and offers the
 * next move.
 */

export type Suggestion = { label: string; onSelect: () => void };

/** Chips the user can click instead of retyping. Also how a clarification is answered. */
export const SuggestedActions: React.FC<{ actions: Suggestion[]; label?: string }> = ({
  actions,
  label,
}) => {
  // A wall of buttons is the same noise problem in a different form, so this caps at three.
  const shown = (actions || []).filter((a) => a && a.label).slice(0, 3);
  if (!shown.length) return null;
  return (
    <div className="mt-2">
      {label ? <div className={`${TYPE.meta} text-slate-600 mb-1.5`}>{label}</div> : null}
      <div className="flex flex-wrap gap-1.5">
        {shown.map((action) => (
          <button
            key={action.label}
            type="button"
            onClick={action.onSelect}
            className={[
              'rounded-full border px-3 py-1.5', TYPE.meta, 'font-medium',
              'border-primary-500/40 text-primary-600 bg-white hover:bg-primary-50',
              FOCUS,
            ].join(' ')}
          >
            {action.label}
          </button>
        ))}
      </div>
    </div>
  );
};

/**
 * Nothing matched.
 *
 * States the fact in bold and then gives somewhere to go. "No matches found." on its own tells the
 * user nothing about whether they asked wrongly or the record does not exist.
 */
export const EmptyState: React.FC<{ title: string; detail?: string; actions?: Suggestion[] }> = ({
  title, detail, actions = [],
}) => (
  <div className={`border ${SURFACE.line} rounded-lg bg-slate-50 px-3 py-3`}>
    <div className="flex items-start gap-2">
      <SearchX aria-hidden="true" size={18} className="text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className={`${TYPE.body} font-semibold text-slate-900`}>{title}</p>
        {detail ? <p className={`${TYPE.meta} text-slate-600 mt-0.5`}>{detail}</p> : null}
        <SuggestedActions actions={actions} label={actions.length ? 'Try:' : undefined} />
      </div>
    </div>
  </div>
);

/**
 * Something failed.
 *
 * Never the raw error. A stack trace or a driver message tells a ship chandler nothing and leaks
 * implementation into a business screen; the same rule the server applies with AppError codes.
 */
export const ErrorState: React.FC<{ message?: string; onRetry?: () => void }> = ({
  message = 'Something went wrong while looking that up.',
  onRetry,
}) => (
  <div className="border border-red-200 rounded-lg bg-red-50 px-3 py-3">
    <div className="flex items-start gap-2">
      <AlertTriangle aria-hidden="true" size={18} className="text-red-500 mt-0.5 shrink-0" />
      <div className="min-w-0">
        <p className={`${TYPE.body} font-semibold text-red-900`}>{message}</p>
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className={`mt-1.5 rounded-md border border-red-300 bg-white px-2.5 py-1 ${TYPE.meta} font-medium text-red-800 hover:bg-red-100 ${FOCUS}`}
          >
            Try again
          </button>
        ) : null}
      </div>
    </div>
  </div>
);

/**
 * Something is happening, and it can be stopped.
 *
 * The old indicator was three pulsing dots that said nothing, and there was no way to abandon a
 * slow request — with no timeout on the model call, a hung request left the panel stuck for good.
 */
export const LoadingIndicator: React.FC<{ label?: string; onCancel?: () => void }> = ({
  label = 'Looking that up…',
  onCancel,
}) => (
  <div className="flex items-center gap-2" role="status" aria-live="polite">
    <Loader2
      aria-hidden="true"
      size={15}
      className="animate-spin motion-reduce:animate-none text-primary-500 shrink-0"
    />
    <span className={`${TYPE.meta} text-slate-600`}>{label}</span>
    {onCancel ? (
      <button
        type="button"
        onClick={onCancel}
        className={`inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 ${TYPE.meta} text-slate-500 hover:text-slate-800 hover:bg-slate-100 ${FOCUS}`}
      >
        <X aria-hidden="true" size={12} />
        Stop
      </button>
    ) : null}
  </div>
);
